'use client';

import React from 'react';

// Import only used icons individually to enable tree-shaking
import {
  QuestionMarkCircleIcon,
  XMarkIcon,
  ChartBarIcon,
  ExclamationCircleIcon,
  InformationCircleIcon,
  ChartPieIcon,
  StarIcon,
  MagnifyingGlassIcon,
  PlusIcon,
  ChevronUpIcon,
  BellIcon,
  ArrowUpIcon,
  ArrowDownIcon,
  ArrowTrendingUpIcon,
  CalendarIcon,
  ArrowTopRightOnSquareIcon,
  BriefcaseIcon,
  ClockIcon,
  BanknotesIcon,
  ArrowLeftIcon,
  HomeIcon,
  Squares2X2Icon,
  Bars3Icon,
  CurrencyDollarIcon,
  FireIcon,
  UserCircleIcon,
  CreditCardIcon,
  SignalIcon,
} from '@heroicons/react/24/outline';
import {
  ExclamationCircleIcon as ExclamationCircleIconSolid,
  InformationCircleIcon as InformationCircleIconSolid,
  BellIcon as BellIconSolid,
  ArrowUpIcon as ArrowUpIconSolid,
  ArrowDownIcon as ArrowDownIconSolid,
  PlusIcon as PlusIconSolid,
  BriefcaseIcon as BriefcaseIconSolid,
  ClockIcon as ClockIconSolid,
  BanknotesIcon as BanknotesIconSolid,
  NewspaperIcon,
  LinkIcon,
  CheckIcon,
} from '@heroicons/react/24/solid';

type IconVariant = 'outline' | 'solid';

// Map icon names to their actual components
const ICON_MAP_OUTLINE: Record<string, React.ComponentType<React.SVGProps<SVGSVGElement>>> = {
  QuestionMarkCircleIcon,
  XMarkIcon,
  ChartBarIcon,
  ExclamationCircleIcon,
  InformationCircleIcon,
  ChartPieIcon,
  StarIcon,
  MagnifyingGlassIcon,
  PlusIcon,
  ChevronUpIcon,
  BellIcon,
  ArrowUpIcon,
  ArrowDownIcon,
  ArrowTrendingUpIcon,
  CalendarIcon,
  ArrowTopRightOnSquareIcon,
  BriefcaseIcon,
  ClockIcon,
  BanknotesIcon,
  ArrowLeftIcon,
  HomeIcon,
  Squares2X2Icon,
  Bars3Icon,
  CurrencyDollarIcon,
  FireIcon,
  UserCircleIcon,
  CreditCardIcon,
  SignalIcon,
};

const ICON_MAP_SOLID: Record<string, React.ComponentType<React.SVGProps<SVGSVGElement>>> = {
  ExclamationCircleIcon: ExclamationCircleIconSolid,
  InformationCircleIcon: InformationCircleIconSolid,
  BellIcon: BellIconSolid,
  ArrowUpIcon: ArrowUpIconSolid,
  ArrowDownIcon: ArrowDownIconSolid,
  PlusIcon: PlusIconSolid,
  BriefcaseIcon: BriefcaseIconSolid,
  ClockIcon: ClockIconSolid,
  BanknotesIcon: BanknotesIconSolid,
  NewspaperIcon,
  LinkIcon,
  CheckIcon,
};

interface IconProps {
    name: string;
    variant?: IconVariant;
    size?: number;
    className?: string;
    onClick?: () => void;
    disabled?: boolean;
    [key: string]: any;
}

function Icon({
    name,
    variant = 'outline',
    size = 24,
    className = '',
    onClick,
    disabled = false,
    ...props
}: IconProps) {
    // Handle special cases
    if (name === 'spinner') {
        return (
            <svg
                width={size}
                height={size}
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                className={className}
                {...props}
            >
                <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" fill="none" opacity="0.3" />
                <path
                    d="M22 12c0-5.523-4.477-10-10-10"
                    stroke="currentColor"
                    strokeWidth="2"
                    fill="none"
                    strokeLinecap="round"
                />
            </svg>
        );
    }

    // Get icon from variant-specific map
    const iconMap = variant === 'solid' ? ICON_MAP_SOLID : ICON_MAP_OUTLINE;
    const IconComponent = iconMap[name] as React.ComponentType<any>;

    if (!IconComponent) {
        console.warn(`Icon "${name}" not found in ${variant} variant`);
        return null;
    }

    return (
        <IconComponent
            width={size}
            height={size}
            className={`${disabled ? 'opacity-50 cursor-not-allowed' : onClick ? 'cursor-pointer hover:opacity-80' : ''} ${className}`}
            onClick={disabled ? undefined : onClick}
            {...props}
        />
    );
}

export default Icon; 